import { onError } from '@apollo/client/link/error'
import { gqlClient } from 'gql'
import type { GraphQLError } from 'graphql'
import queryString from 'query-string'
import { matchPath } from 'react-router'

import { notify } from 'components/uiKit/Notification'
import { NotificationType } from 'components/uiKit/Notification/types'
import { APP_PATHS, AUTH_PATHS } from 'constants/paths'
import { ErrorMessage } from 'gql/__generated__/graphql'
import { companySlug } from 'gql/companies/gql/queries'
import generateLink from 'routes/generateLink'
import history from 'routes/history'
import existKeyLocal from 'services/Translation/existKeyLocal'
import { getBackendErrorMessage } from 'utils/errors'

type ExcludedErrors =
  | ErrorMessage.EMPLOYEES_LIMIT_REACHED
  | ErrorMessage.STORAGE_LIMIT_REACHED
  | ErrorMessage.CANT_GET_BRAND

type NotFoundErrorKeys =
  | 'SECTION_NOT_FOUND'
  | 'PARENT_FILE_META_GROUP_NOT_FOUND'
  | 'PARENT_PROJECT_GROUP_NOT_FOUND'
  | 'FILE_META_GROUP_NOT_FOUND'
  | 'PROJECT_GROUP_NOT_FOUND'
  | 'PROJECT_NOT_FOUND'
  | 'TASK_NOT_FOUND'
  | 'TAG_NOT_FOUND'
  | 'EDITOR_SECTION_NOT_FOUND'
  | 'BAD_SECTION'
  | 'PermissionError'

const NOT_FOUND_PAGE_ERRORS: (ExcludedErrors | NotFoundErrorKeys)[] = [
  ErrorMessage.EMPLOYEES_LIMIT_REACHED,
  ErrorMessage.STORAGE_LIMIT_REACHED,
  // errors returned by BE on pages NotFound
  'SECTION_NOT_FOUND',
  'PARENT_FILE_META_GROUP_NOT_FOUND',
  'PARENT_PROJECT_GROUP_NOT_FOUND',
  'FILE_META_GROUP_NOT_FOUND',
  'PROJECT_GROUP_NOT_FOUND',
  'PROJECT_NOT_FOUND',
  'TASK_NOT_FOUND',
  'TAG_NOT_FOUND',
  'EDITOR_SECTION_NOT_FOUND',
  'BAD_SECTION',
  ErrorMessage.CANT_GET_BRAND,
  'PermissionError',
]

// check if there is an error on page NotFound
const errorNotFound = (graphQLErrors: ReadonlyArray<GraphQLError> | undefined) =>
  graphQLErrors?.find(({ message }) =>
    NOT_FOUND_PAGE_ERRORS.some((errorInPlug) => message.includes(errorInPlug)),
  )

const errorRedirect = [
  'PROJECT_GROUP_DOESNT_EXIST_IN_COMPANY',
  // 'SECTION_DOESNT_EXIST',
  // 'DOESNT_HAVE_PERMISSION',
]

const logout = () => {
  const { redirectTo = '' } = queryString.parse(history.location.search || '')
  const redirectLink = generateLink(AUTH_PATHS.login, {
    search: `?redirectTo=${encodeURIComponent(String(redirectTo) || history.location.pathname)}`,
  })
  history.replace(redirectLink)
}

const ssoLogin = async () => {
  const match = matchPath(history.location.pathname, APP_PATHS.company)
  const companyId = match ? (match.params as { companyId: string }).companyId : undefined
  const { data } = await gqlClient.core.query({
    query: companySlug,
    variables: {
      companyId: '',
    },
  })

  const link = generateLink(AUTH_PATHS.sso, {
    ssoId: data.data || companyId,
  })
  history.replace(link)
}

const dedupeErrors = (message: string, cb: (id: string) => void) => {
  const id = (message || '').replace(/\W/g, '_').toLowerCase()

  let isVisible = id === ''
  if (!isVisible) {
    const el = document.querySelector(`.${id}`)
    isVisible = el === null
  }
  if (isVisible) {
    cb(id)
  }
}

export const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  const { response } = operation.getContext()

  if (response?.status === 401) {
    logout()
    return
  }

  if (errorNotFound(graphQLErrors)) {
    return
  }

  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path, extensions, source }) => {
      // Handle authentication errors with 401 code
      if (extensions?.code === '401') {
        logout()
        return
      }

      // redirect subscribtions
      if (errorRedirect.includes(message)) {
        window.location.hash = APP_PATHS.home
      }

      if (message === ErrorMessage.SSO_PROVIDER_UNAUTHORIZED) {
        ssoLogin()
        return
      }
      if (message === ErrorMessage.SSO_DEFAULT_PROVIDER_UNAUTHORIZED) {
        logout()
        return
      }
      // Handle SSO_REQUIRED error with redirect
      if (message === ErrorMessage.SSO_REQUIRED || extensions?.code === 'SSO_REQUIRED') {
        console.warn('SSO_REQUIRED in errorLink:', { message, extensions })
        const companyId = typeof extensions?.companyId === 'string' ? extensions.companyId : ''
        if (companyId) {
          const ssoLink = generateLink(AUTH_PATHS.sso, { ssoId: companyId })
          history.replace(ssoLink)
          return
        }
      }
      // Handle ACCESS_DENIED error - redirect to login
      if (extensions?.code === 'ACCESS_DENIED') {
        console.warn('ACCESS_DENIED in errorLink:', { message, extensions })
        logout()
        return
      }
      // known errors with localization
      if (existKeyLocal(`be.error.${message}`)) {
        dedupeErrors(message, (id) => {
          notify({
            type: NotificationType.error,
            message: getBackendErrorMessage(message),
            className: id,
          })
        })
        return
      }

      // TODO: this condition is questionable, possibly irrelevant
      if (message === 'Cannot return null for non-nullable field Query.getKUser.') {
        logout()
      } else {
        const { code, stacktrace } = extensions || {}
        // unknown errors
        console.error({ code, stacktrace, message, path, locations, source })
        dedupeErrors(message, (id) => {
          notify({
            type: NotificationType.error,
            message: `[GraphQL error]`,
            // description: `Message: ${message}, Location: ${JSON.stringify(
            //   locations,
            // )}, Path: ${path}`,
            className: id,
          })
        })
      }
    })
  }

  if (networkError && networkError.message === 'Failed to fetch') {
    dedupeErrors('NETWORK_ERROR', (id) => {
      notify({
        type: NotificationType.error,
        message: getBackendErrorMessage('NETWORK_ERROR'),
        className: id,
      })
    })
  }

  return
})
